/**
 * Audio Features Utilities
 */

import type { AudioFeatures } from '../api/types';

/**
 * Format BPM for display (round to integer or 1 decimal)
 */
export function formatBpm(tempo: number): string {
  const rounded = Math.round(tempo);
  return rounded === tempo ? rounded.toString() : tempo.toFixed(1);
}

/**
 * Format duration in milliseconds to human-readable format
 * - Under 1 hour: "mm:ss" (e.g., "45:30")
 * - 1-23 hours: "Xh Ym" (e.g., "2h 15m")
 * - 24+ hours: "Xd Yh" (e.g., "1d 6h")
 */
export function formatDuration(ms: number): string {
  const totalSeconds = Math.floor(ms / 1000);
  const totalMinutes = Math.floor(totalSeconds / 60);
  const totalHours = Math.floor(totalMinutes / 60);
  const totalDays = Math.floor(totalHours / 24);

  // Under 1 hour: show mm:ss
  if (totalHours === 0) {
    const minutes = totalMinutes;
    const seconds = totalSeconds % 60;
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  }

  // 24+ hours: show days and hours
  if (totalDays > 0) {
    const hours = totalHours % 24;
    if (hours === 0) {
      return `${totalDays}d`;
    }
    return `${totalDays}d ${hours}h`;
  }

  // 1-23 hours: show hours and minutes
  const hours = totalHours;
  const minutes = totalMinutes % 60;
  if (minutes === 0) {
    return `${hours}h`;
  }
  return `${hours}h ${minutes}m`;
}

/**
 * Format ISO timestamp to relative time (e.g., "2 hours ago", "5 minutes ago")
 * For history view timestamps
 */
export function formatRelativeTime(isoTimestamp: string): string {
  const now = Date.now();
  const timestamp = new Date(isoTimestamp).getTime();
  const diffMs = now - timestamp;

  const seconds = Math.floor(diffMs / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  if (seconds < 60) {
    return 'just now';
  }
  if (minutes < 60) {
    return `${minutes} ${minutes === 1 ? 'minute' : 'minutes'} ago`;
  }
  if (hours < 24) {
    return `${hours} ${hours === 1 ? 'hour' : 'hours'} ago`;
  }
  if (days < 7) {
    return `${days} ${days === 1 ? 'day' : 'days'} ago`;
  }
  if (days < 30) {
    const weeks = Math.floor(days / 7);
    return `${weeks} ${weeks === 1 ? 'week' : 'weeks'} ago`;
  }
  if (days < 365) {
    const months = Math.floor(days / 30);
    return `${months} ${months === 1 ? 'month' : 'months'} ago`;
  }
  const years = Math.floor(days / 365);
  return `${years} ${years === 1 ? 'year' : 'years'} ago`;
}

/**
 * Format ISO timestamp to absolute time with 24h clock (e.g., "19.1.2026 14:35")
 */
export function formatAbsoluteTime(isoTimestamp: string): string {
  const date = new Date(isoTimestamp);
  const day = date.getDate();
  const month = date.getMonth() + 1; // getMonth() is 0-indexed
  const year = date.getFullYear();
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');

  return `${day}.${month}.${year} ${hours}:${minutes}`;
}

/**
 * Format played at timestamp for history view
 * Shows absolute time with relative time in parentheses (e.g., "19.1.2026 14:35 (2 hours ago)")
 */
export function formatPlayedAt(isoTimestamp: string): string {
  const absolute = formatAbsoluteTime(isoTimestamp);
  const relative = formatRelativeTime(isoTimestamp);
  return `${absolute} (${relative})`;
}

/**
 * Get energy level label
 */
export function getEnergyLabel(energy: number): string {
  if (energy >= 0.8) return 'Very High';
  if (energy >= 0.6) return 'High';
  if (energy >= 0.4) return 'Medium';
  if (energy >= 0.2) return 'Low';
  return 'Very Low';
}

/**
 * Get energy color class
 */
export function getEnergyColor(energy: number): string {
  if (energy >= 0.8) return 'bg-red-500';
  if (energy >= 0.6) return 'bg-orange-500';
  if (energy >= 0.4) return 'bg-yellow-500';
  if (energy >= 0.2) return 'bg-green-500';
  return 'bg-blue-500';
}

/**
 * Get valence (mood) label
 */
export function getValenceLabel(valence: number): string {
  if (valence >= 0.8) return 'Very Happy';
  if (valence >= 0.6) return 'Happy';
  if (valence >= 0.4) return 'Neutral';
  if (valence >= 0.2) return 'Melancholic';
  return 'Sad';
}

/**
 * Calculate BPM compatibility score (0-1)
 * Considers half-time and double-time relationships
 */
export function getBpmCompatibility(bpm1: number, bpm2: number): number {
  // Normalize to reference range
  const normalize = (bpm: number) => {
    while (bpm > 160) bpm /= 2;
    while (bpm < 80) bpm *= 2;
    return bpm;
  };

  const n1 = normalize(bpm1);
  const n2 = normalize(bpm2);
  const diff = Math.abs(n1 - n2);

  // Perfect match
  if (diff < 1) return 1;

  // Very close (within 3 BPM)
  if (diff < 3) return 0.95;

  // Mixable range (within 6 BPM)
  if (diff < 6) return 0.8;

  // Acceptable (within 10 BPM)
  if (diff < 10) return 0.6;

  // Difficult
  return Math.max(0, 1 - diff / 40);
}

/**
 * Calculate overall transition score between two tracks
 */
export function getTransitionScore(
  from: AudioFeatures,
  to: AudioFeatures,
  weights: { bpm?: number; key?: number; energy?: number } = {}
): number {
  const { bpm = 1, key = 1, energy = 0.5 } = weights;

  // BPM compatibility (0-1)
  const bpmScore = getBpmCompatibility(from.tempo, to.tempo);

  // Key compatibility (0-1) - simplified, use getKeyDistance for full calculation
  const keyDiff = Math.abs(from.key - to.key);
  const keyScore = keyDiff === 0 ? 1 : keyDiff <= 2 ? 0.8 : keyDiff <= 5 ? 0.5 : 0.2;

  // Energy difference (0-1)
  const energyDiff = Math.abs(from.energy - to.energy);
  const energyScore = 1 - energyDiff;

  // Weighted average
  const totalWeight = bpm + key + energy;
  return (bpmScore * bpm + keyScore * key + energyScore * energy) / totalWeight;
}

/**
 * Categorize a track based on its features
 */
export function categorizeTrack(features: AudioFeatures): string[] {
  const categories: string[] = [];

  // Energy categories
  if (features.energy >= 0.7 && features.tempo >= 120) {
    categories.push('High Energy');
  }
  if (features.energy <= 0.4 && features.acousticness >= 0.5) {
    categories.push('Chill');
  }

  // Danceability
  if (features.danceability >= 0.7) {
    categories.push('Danceable');
  }

  // Vocal content
  if (features.instrumentalness >= 0.5) {
    categories.push('Instrumental');
  }
  if (features.speechiness >= 0.3) {
    categories.push('Vocal-heavy');
  }

  // Live recording
  if (features.liveness >= 0.8) {
    categories.push('Live');
  }

  // Acoustic
  if (features.acousticness >= 0.7) {
    categories.push('Acoustic');
  }

  // Mood
  if (features.valence >= 0.7) {
    categories.push('Uplifting');
  }
  if (features.valence <= 0.3) {
    categories.push('Moody');
  }

  return categories;
}

/**
 * Calculate playlist statistics
 */
export function calculatePlaylistStats(features: AudioFeatures[]): {
  avgEnergy: number;
  avgTempo: number;
  avgValence: number;
  avgDanceability: number;
  totalDurationMs: number;
  tempoRange: [number, number];
  energyRange: [number, number];
} {
  if (features.length === 0) {
    return {
      avgEnergy: 0,
      avgTempo: 0,
      avgValence: 0,
      avgDanceability: 0,
      totalDurationMs: 0,
      tempoRange: [0, 0],
      energyRange: [0, 0],
    };
  }

  const sum = features.reduce(
    (acc, f) => ({
      energy: acc.energy + f.energy,
      tempo: acc.tempo + f.tempo,
      valence: acc.valence + f.valence,
      danceability: acc.danceability + f.danceability,
      duration: acc.duration + (f.durationMs || 0),
    }),
    { energy: 0, tempo: 0, valence: 0, danceability: 0, duration: 0 }
  );

  const tempos = features.map(f => f.tempo);
  const energies = features.map(f => f.energy);

  return {
    avgEnergy: sum.energy / features.length,
    avgTempo: sum.tempo / features.length,
    avgValence: sum.valence / features.length,
    avgDanceability: sum.danceability / features.length,
    totalDurationMs: sum.duration,
    tempoRange: [Math.min(...tempos), Math.max(...tempos)],
    energyRange: [Math.min(...energies), Math.max(...energies)],
  };
}
